import React, { useState, useEffect } from 'react';
import { getDayWiseStats } from '../services/babyMovementService';

const DayWiseStatsChart = ({ userId, refreshTrigger }) => {
  const [dayWiseData, setDayWiseData] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState('combined'); // 'combined', 'rolls', 'kicks', 'total'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

  useEffect(() => {
    if (userId) {
      fetchDayWiseStats();
    }
  }, [userId, refreshTrigger]);

  const fetchDayWiseStats = async () => {
    try {
      setLoading(true);
      const stats = await getDayWiseStats(userId);
      setDayWiseData(stats);
    } catch (error) {
      console.error('Error fetching day-wise stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Convert object to array and sort by date
  const getChartData = () => {
    const dataArray = Object.entries(dayWiseData).map(([date, stats]) => ({
      date,
      rolls: stats.rolls || 0,
      kicks: stats.kicks || 0,
      total: (stats.rolls || 0) + (stats.kicks || 0),
      count: stats.count || 0
    }));

    // Sort by date
    dataArray.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return dataArray;
  };

  const chartData = getChartData();

  // Calculate max value for scaling
  const getMaxValue = () => {
    if (chartData.length === 0) return 1;
    
    if (viewType === 'combined') {
      return Math.max(...chartData.map(d => Math.max(d.rolls, d.kicks, d.total)));
    } else if (viewType === 'rolls') {
      return Math.max(...chartData.map(d => d.rolls));
    } else if (viewType === 'kicks') {
      return Math.max(...chartData.map(d => d.kicks));
    } else {
      return Math.max(...chartData.map(d => d.total));
    }
  };

  const maxValue = getMaxValue();

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">📊 Day-wise Statistics</h2>
        <div className="text-center py-8">
          <div className="text-4xl mb-4">📈</div>
          <p className="text-gray-600">No data available yet</p>
          <p className="text-sm text-gray-500 mt-2">Start tracking movements to see statistics</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 md:mb-0">
          📊 Day-wise Statistics
        </h2>
        
        <div className="flex flex-wrap gap-2">
          {/* View Type Selector */}
          <select
            value={viewType}
            onChange={(e) => setViewType(e.target.value)}
            className="border-2 border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold focus:border-green-600 focus:outline-none"
          >
            <option value="combined">📊 All Metrics</option>
            <option value="rolls">🔄 Rolls Only</option>
            <option value="kicks">👣 Kicks Only</option>
            <option value="total">📈 Total Count</option>
          </select>

          {/* Sort Order Selector */}
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="border-2 border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold hover:bg-gray-100 transition-colors"
          >
            {sortOrder === 'asc' ? '📅 Oldest First' : '📅 Newest First'}
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Days Tracked</p>
          <p className="text-2xl font-bold text-gray-900">{chartData.length}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Total Rolls</p>
          <p className="text-2xl font-bold text-green-600">
            {chartData.reduce((sum, d) => sum + d.rolls, 0)}
          </p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Total Kicks</p>
          <p className="text-2xl font-bold text-blue-600">
            {chartData.reduce((sum, d) => sum + d.kicks, 0)}
          </p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-1">Avg Per Day</p>
          <p className="text-2xl font-bold text-purple-600">
            {Math.round(chartData.reduce((sum, d) => sum + d.total, 0) / chartData.length)}
          </p>
        </div>
      </div>

      {/* Legend */}
      {viewType === 'combined' && (
        <div className="flex flex-wrap gap-4 mb-4 text-sm">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
            <span className="text-gray-700">Rolls</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-blue-500 rounded mr-2"></div>
            <span className="text-gray-700">Kicks</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-purple-500 rounded mr-2"></div>
            <span className="text-gray-700">Total</span>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="overflow-x-auto">
        <div className="min-w-full" style={{ minWidth: `${chartData.length * 80}px` }}>
          {/* Chart bars */}
          <div className="flex items-end justify-start gap-2 md:gap-4 border-l-2 border-b-2 border-gray-300 pl-2 pb-2 pt-12" style={{ height: '340px' }}>
            {chartData.map((data, index) => (
              <div key={data.date} className="flex flex-col items-center" style={{ minWidth: '60px', height: '100%' }}>
                {/* Bars container */}
                <div className="flex items-end justify-center gap-1 w-full" style={{ height: '280px' }}>
                  {viewType === 'combined' ? (
                    <>
                      {/* Rolls bar */}
                      <div className="flex flex-col items-center justify-end" style={{ height: '100%' }}>
                        <div className="flex flex-col items-center" style={{ height: '100%' }}>
                          <div className="text-xs font-bold text-green-700 mb-1">{data.rolls}</div>
                          <div className="flex-1 flex flex-col justify-end w-full">
                            <div
                              className="bg-green-500 rounded-t transition-all duration-300 hover:bg-green-600 cursor-pointer"
                              style={{
                                height: `${(data.rolls / maxValue) * 100}%`,
                                minHeight: data.rolls > 0 ? '4px' : '0',
                                width: '20px'
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Kicks bar */}
                      <div className="flex flex-col items-center justify-end" style={{ height: '100%' }}>
                        <div className="flex flex-col items-center" style={{ height: '100%' }}>
                          <div className="text-xs font-bold text-blue-700 mb-1">{data.kicks}</div>
                          <div className="flex-1 flex flex-col justify-end w-full">
                            <div
                              className="bg-blue-500 rounded-t transition-all duration-300 hover:bg-blue-600 cursor-pointer"
                              style={{
                                height: `${(data.kicks / maxValue) * 100}%`,
                                minHeight: data.kicks > 0 ? '4px' : '0',
                                width: '20px'
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Total bar */}
                      <div className="flex flex-col items-center justify-end" style={{ height: '100%' }}>
                        <div className="flex flex-col items-center" style={{ height: '100%' }}>
                          <div className="text-xs font-bold text-purple-700 mb-1">{data.total}</div>
                          <div className="flex-1 flex flex-col justify-end w-full">
                            <div
                              className="bg-purple-500 rounded-t transition-all duration-300 hover:bg-purple-600 cursor-pointer"
                              style={{
                                height: `${(data.total / maxValue) * 100}%`,
                                minHeight: data.total > 0 ? '4px' : '0',
                                width: '20px'
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-end w-full" style={{ height: '100%' }}>
                      <div className="flex flex-col items-center" style={{ height: '100%' }}>
                        <div className={`text-xs font-bold mb-1 ${
                          viewType === 'rolls' ? 'text-green-700' :
                          viewType === 'kicks' ? 'text-blue-700' :
                          'text-purple-700'
                        }`}>
                          {viewType === 'rolls' ? data.rolls :
                           viewType === 'kicks' ? data.kicks :
                           data.total}
                        </div>
                        <div className="flex-1 flex flex-col justify-end w-full">
                          <div
                            className={`rounded-t transition-all duration-300 cursor-pointer ${
                              viewType === 'rolls' ? 'bg-green-500 hover:bg-green-600' :
                              viewType === 'kicks' ? 'bg-blue-500 hover:bg-blue-600' :
                              'bg-purple-500 hover:bg-purple-600'
                            }`}
                            style={{
                              height: `${((viewType === 'rolls' ? data.rolls : viewType === 'kicks' ? data.kicks : data.total) / maxValue) * 100}%`,
                              minHeight: (viewType === 'rolls' ? data.rolls : viewType === 'kicks' ? data.kicks : data.total) > 0 ? '4px' : '0',
                              width: '40px'
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Date label */}
                <div className="text-xs text-gray-600 mt-2 text-center transform -rotate-45 origin-top-left whitespace-nowrap">
                  {formatDate(data.date)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="mt-6 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left font-semibold text-gray-700">Date</th>
              <th className="px-4 py-2 text-center font-semibold text-gray-700">Rolls</th>
              <th className="px-4 py-2 text-center font-semibold text-gray-700">Kicks</th>
              <th className="px-4 py-2 text-center font-semibold text-gray-700">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {chartData.map((data) => (
              <tr key={data.date} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-900">{data.date}</td>
                <td className="px-4 py-2 text-center text-green-600 font-semibold">{data.rolls}</td>
                <td className="px-4 py-2 text-center text-blue-600 font-semibold">{data.kicks}</td>
                <td className="px-4 py-2 text-center text-purple-600 font-semibold">{data.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DayWiseStatsChart;
